/**
 * Lernen S4 — die Rundungsregel in Einstellungen → Zahlungen („Preise“). Konzept: „Du rundest den
 * Menüpreis pro Gast auf volle 5 € auf“ wird eine Einstellung, die die Preis-Engine ausführt — nie
 * eine Regel für die KI. Setzen kann sie der Betreiber hier oder mit „Als Einstellung übernehmen“
 * auf „So lerne ich“. Der Server rechnet (packages/pricing#preisRundungAnwenden), diese Karte zeigt
 * und speichert nur die Regel.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiMutate, useApi } from "../hooks/useApi";
import { Button, ErrorNote, Icon, LoadingRows, Select } from "./components";

interface Regel {
  schrittCents: 100 | 500 | 1000;
  richtung: "auf" | "ab";
  ausSchlussId: string | null;
  geaendertAm: string;
}
interface Antwort {
  eingerichtet: boolean;
  regel: Regel | null;
}

const AUS = "aus";
const OPTIONEN = [
  { value: AUS, label: "Nicht runden" },
  { value: "500:auf", label: "Auf volle 5 € aufrunden" },
  { value: "100:auf", label: "Auf volle Euro aufrunden" },
  { value: "1000:auf", label: "Auf volle 10 € aufrunden" },
  { value: "500:ab", label: "Auf volle 5 € abrunden" },
  { value: "100:ab", label: "Auf volle Euro abrunden" },
  { value: "1000:ab", label: "Auf volle 10 € abrunden" },
];

const wertVon = (r: Regel | null) => (r ? `${r.schrittCents}:${r.richtung}` : AUS);

export function PreisRundungKarte({ canEdit }: { canEdit: boolean }) {
  const q = useApi<Antwort>("/api/settings/preisrundung");
  const [wert, setWert] = useState(AUS);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState(false);
  useEffect(() => {
    if (q.data) setWert(wertVon(q.data.regel));
  }, [q.data]);

  if (q.error) return <ErrorNote message={q.error} onRetry={q.reload} />;
  if (!q.data) return <LoadingRows rows={2} />;
  const d = q.data;
  const unveraendert = wert === wertVon(d.regel);

  async function speichern() {
    setBusy(true);
    setFehler(null);
    setGespeichert(false);
    try {
      const [schritt, richtung] = wert.split(":");
      await apiMutate("/api/settings/preisrundung", "PUT", { regel: wert === AUS ? null : { schrittCents: Number(schritt), richtung } });
      setGespeichert(true);
      q.reload();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="max-w-3xl space-y-3" data-testid="preisrundung-karte">
      <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Preisrundung für KI-Entwürfe</h3>
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Rundet den Menüpreis pro Gast in neuen KI-Entwürfen. Die Preis-Engine ändert dafür den Preis der teuersten Einzelportion um den Unterschied — so, wie du es sonst von Hand machst. Bestehende und gesendete Angebote bleiben unverändert, und die KI selbst setzt nie einen Preis.
      </p>
      {!d.eingerichtet ? (
        <p className="rounded-lg bg-surface-container-high px-3 py-2 font-body-sm text-body-sm text-on-surface-variant dark:bg-white/10">
          Die Preisrundung braucht ein Datenbank-Update, das noch aussteht.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full sm:w-72">
              <Select ariaLabel="Preisrundung" value={wert} onValueChange={(v) => { setWert(v); setGespeichert(false); }} options={OPTIONEN} disabled={!canEdit || busy} />
            </div>
            <Button type="button" className="!py-2" disabled={!canEdit || busy || unveraendert} onClick={() => void speichern()}>
              {busy ? "Speichert …" : "Speichern"}
            </Button>
            {gespeichert && unveraendert && (
              <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-tertiary dark:text-tertiary-fixed-dim"><Icon name="check_circle" className="text-[18px]" /> Gespeichert</span>
            )}
          </div>
          {d.regel?.ausSchlussId && unveraendert && (
            <p className="font-body-sm text-[12.5px] text-on-surface-variant">
              Übernommen aus deinen Änderungen an KI-Entwürfen (<Link to="/gedaechtnis?tab=so-lerne-ich" className="font-semibold text-primary hover:underline">So lerne ich</Link>).
            </p>
          )}
          {!canEdit && <p className="font-body-sm text-[12.5px] text-on-surface-variant">Ändern können das nur Inhaber.</p>}
        </>
      )}
      {fehler && <ErrorNote message={fehler} />}
    </section>
  );
}
